import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, X, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * EmailVerificationBanner — barra superior persistente exibida apenas quando
 * o usuário logado ainda não verificou o email.
 *
 * Não é bloqueante: durante o grace period (7 dias) o usuário navega e cadastra
 * negócios livremente. Rotas STRICT (avaliar/comentar/resgatar cupom/editar)
 * já retornam 403 mesmo dentro do grace, e após 7 dias todo request protegido
 * volta a bloquear.
 *
 * Esconde:
 *  • Se o usuário não está logado
 *  • Se o email já está verificado
 *  • Na própria página /verificar-email (evita redundância)
 *  • Quando o usuário clicou em "fechar" nesta sessão (sessionStorage)
 */
const SESSION_DISMISS_KEY = 'email_verification_banner_dismissed';
const GRACE_DAYS = 7;

function computeDaysLeft(user) {
  const createdIso = user?.data_criacao || user?.created_at;
  if (!createdIso) return null;
  const created = new Date(createdIso);
  if (Number.isNaN(created.getTime())) return null;
  const expiresAt = new Date(created.getTime() + GRACE_DAYS * 24 * 60 * 60 * 1000);
  const now = new Date();
  const diffMs = expiresAt.getTime() - now.getTime();
  const days = Math.ceil(diffMs / (24 * 60 * 60 * 1000));
  return days; // pode ser negativo se já expirou
}

export default function EmailVerificationBanner() {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(SESSION_DISMISS_KEY) === '1'; }
    catch { return false; }
  });
  const daysLeft = useMemo(() => computeDaysLeft(user), [user]);

  if (!isAuthenticated) return null;
  if (!user || user.email_verificado !== false) return null;
  if (location.pathname.startsWith('/verificar-email')) return null;
  if (dismissed) return null;

  const handleDismiss = () => {
    try { sessionStorage.setItem(SESSION_DISMISS_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  // Estilo mais urgente quando restam <= 2 dias ou já expirou
  const expired = daysLeft !== null && daysLeft <= 0;
  const urgent = daysLeft !== null && daysLeft > 0 && daysLeft <= 2;
  const bg = expired
    ? 'bg-red-50 border-red-200 text-red-900'
    : urgent
    ? 'bg-orange-50 border-orange-200 text-orange-900'
    : 'bg-amber-50 border-amber-200 text-amber-900';
  const iconColor = expired ? 'text-red-600' : urgent ? 'text-orange-600' : 'text-amber-600';

  return (
    <div
      className={`${bg} border-b`}
      role="alert"
      data-testid="email-verification-banner"
    >
      <div className="max-w-6xl mx-auto px-4 py-2 flex items-center gap-3 text-sm">
        {expired ? (
          <Clock className={`w-4 h-4 flex-shrink-0 ${iconColor}`} aria-hidden="true" />
        ) : (
          <Mail className={`w-4 h-4 flex-shrink-0 ${iconColor}`} aria-hidden="true" />
        )}
        <p className="flex-1 leading-snug">
          {expired ? (
            <>
              <strong>Prazo esgotado.</strong>{' '}
              <span className="hidden sm:inline">Verifique seu e-mail para desbloquear cadastros e ações. </span>
              <span className="sm:hidden">Verifique seu e-mail para desbloquear. </span>
            </>
          ) : daysLeft !== null ? (
            <>
              <span className="hidden sm:inline">
                <strong>{daysLeft === 1 ? 'Falta 1 dia' : `Faltam ${daysLeft} dias`}</strong>
                {' '}para confirmar seu e-mail. Depois disso ações ficam bloqueadas.{' '}
              </span>
              <span className="sm:hidden">
                <strong>{daysLeft === 1 ? 'Falta 1 dia' : `${daysLeft} dias`}</strong> pra confirmar.{' '}
              </span>
            </>
          ) : (
            <>
              <span className="hidden sm:inline">Confirme seu e-mail para resgatar cupons, comentar e favoritar negócios. </span>
              <span className="sm:hidden">Confirme seu e-mail para liberar todas as funções. </span>
            </>
          )}
          <Link
            to="/verificar-email?send=1"
            state={{ email: user.email, autoSend: true }}
            onClick={() => {
              try { localStorage.removeItem('email_verify_skipped'); } catch { /* ignore */ }
            }}
            className="font-semibold underline hover:opacity-80"
            data-testid="email-verification-banner-cta"
          >
            Verificar agora
          </Link>
        </p>
        {!expired && (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Fechar aviso"
            className="hover:opacity-70 flex-shrink-0"
            data-testid="email-verification-banner-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
